import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionTitle from '@/components/base/SectionTitle';
import { arcs } from '@/mocks/drama';

export default function PlotSection() {
  const [openIds, setOpenIds] = useState<string[]>([arcs[0].id]);

  const toggle = (id: string) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <section id="plot" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto px-4 md:px-6 max-w-7xl">
        <Reveal>
          <SectionTitle
            eyebrow="剧情篇章"
            title="按四个篇章，看清剧情是怎么一步步走向大结局的"
            description="整部剧的骨架由四个篇章组成。展开你感兴趣的篇章，可以看到该阶段的关键节点；在意剧透的话，建议先看完对应集数再回来对照。"
          />
        </Reveal>

        <div className="mt-10 grid gap-8 grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-8 space-y-3">
            {arcs.map((arc, index) => {
              const open = openIds.includes(arc.id);
              return (
                <Reveal key={arc.id} delay={index * 50}>
                  <div className={`overflow-hidden rounded-lg border transition-colors ${open ? 'border-primary-300 bg-background-50' : 'border-background-200 bg-background-50'}`}>
                    <button
                      type="button"
                      onClick={() => toggle(arc.id)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${open ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-background-200 text-foreground-700'}`}>
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-heading text-base md:text-lg font-bold text-foreground-950">{arc.title}</span>
                        <span className="mt-0.5 block font-label text-xs text-secondary-700">{arc.range}</span>
                      </span>
                      <i className={`shrink-0 text-xl text-foreground-500 transition-transform ${open ? 'ri-subtract-line' : 'ri-add-line'}`} aria-hidden="true" />
                    </button>
                    {open ? (
                      <div className="px-5 pb-5 pl-5 md:pl-[4.5rem]">
                        <p className="font-body text-sm text-foreground-700">{arc.lead}</p>
                        <ul className="mt-4 space-y-3">
                          {arc.points.map((point) => (
                            <li key={point} className="flex items-start gap-3">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                              <span className="font-body text-sm leading-relaxed text-foreground-700">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="lg:col-span-4" delay={120}>
            <aside className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">大结局看点提示</h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">
                大结局篇没有刻意制造新的冲突，而是把重心放在“把话说出口”之后的和解上。三处呼应值得留意：
              </p>
              <ul className="mt-4 space-y-3 font-body text-sm text-foreground-700">
                <li className="flex items-start gap-2">
                  <i className="ri-restaurant-2-line mt-0.5 text-primary-600" aria-hidden="true" />
                  第一集与最后一集的餐桌构图几乎一致，气氛却完全不同。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-open-line mt-0.5 text-primary-600" aria-hidden="true" />
                  早期“没说出口的解释”，在结尾以坦白的方式补齐。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-go-back-line mt-0.5 text-primary-600" aria-hidden="true" />
                  片尾回望镜头把开头伏笔逐一回应，形成闭环。
                </li>
              </ul>
              <a
                href="#episodes"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-secondary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 dark:text-foreground-950 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-list-check-2" aria-hidden="true" />
                回到分集导览
              </a>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}